<template>
  <div>

    <template v-if="isRushVisible">
      <table class="prop-table mb-1">
        <tr>
          <th>Segment</th>
          <td>{{rush.playerGroupId | getPlayerGroupNameVerbose}}</td>
        </tr>
        <tr>
          <th>Client min version</th>
          <td>{{rush.clientMinVersionCode | convertOptionalVersionCodeToStr('not specified')}}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td :class="getRushStatusClass(rush.status)">
            {{rush.status | getRushStatusName}}
          </td>
        </tr>
        <tr>
          <th>Created at</th>
          <td>{{rush.ctime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Planned start</th>
          <td>{{rush.stime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real start</th>
          <td>{{rush.srtime | getRushRealStartTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned duration</th>
          <td>{{plannedDurationStr}}</td>
        </tr>
        <tr>
          <th>Remaining time</th>
          <td>{{remainingTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned finish</th>
          <td>{{rush.ftime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real finish</th>
          <td>{{rush.frtime | getRushRealFinishTimeStr}}</td>
        </tr>
        <tr>
          <th>Real duration</th>
          <td>{{realDurationStr}}</td>
        </tr>
      </table>

      <hr>

      <b-button
        v-if="isEditButtonVisible"
        :to="{name: 'rush_edit', params: {rushId: this.rushId}}"
        variant="primary"
      >
        Edit
      </b-button>

      <b-button
        v-if="isForceStartButtonVisible"
        @click="forceStartRushWithConfirmation"
      >
        Force start
      </b-button>
    </template>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
/**
 * @typedef { import('../auth.js').Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

/**
 * @typedef { import('../model/rush.js').Rush } Rush
 */

import { RUSH_STATUS__INCOMING        } from '../model/rush.js';
import { RUSH_STATUS__ACTIVE          } from '../model/rush.js';
import { mayManageRush                } from '../model/rush.js';
import { getRushStatusName            } from '../model/rush.js';
import { getRushStatusClass           } from '../model/rush.js';
import { getRushRealStartTimeStr      } from '../model/rush.js';
import { getRushRealFinishTimeStr     } from '../model/rush.js';
import { getRushPlannedDurationStr    } from '../model/rush.js';
import { getRushRemainingTimeStr      } from '../model/rush.js';
import { getRushRealDurationStrSimple } from '../model/rush.js';
import { isRushEditable               } from '../model/rush.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { getPlayerGroupNameVerbose } from '../model/event.js';

import LoadingIndicator from './LoadingIndicator.vue';

import NowSecMixin from '../NowSecMixin.js';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'RushInfo',

  components: {
    LoadingIndicator,
  },

  mixins: [
    NowSecMixin,
  ],

  props: {
    rushId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadingStatus: LOADING_STATUS__INIT,

      /** @type {?Rush} */
      rush: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    mayManageRush() {
      return mayManageRush(this.actor.role);
    },

    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.rush === null) {
        return `Rush with id ${this.rushId} does not exist.`;
      }
      return null;
    },

    isRushVisible() {
      return this.isLoadedNormal && (this.rush !== null);
    },

    /** @returns {?string} */
    plannedDurationStr() {
      const rush = this.rush;
      if(rush === null) {
        return null;
      }

      return getRushPlannedDurationStr(rush);
    },

    /** @returns {?string} */
    remainingTimeStr() {
      const rush = this.rush;
      if(rush === null) {
        return null;
      }

      return getRushRemainingTimeStr(rush, this.nowSec);
    },

    /** @returns {?string} */
    realDurationStr() {
      const rush = this.rush;
      if(rush === null) {
        return null;
      }

      return getRushRealDurationStrSimple(rush);
    },

    /** @returns {boolean} */
    isEditButtonVisible() {
      if(!this.mayManageRush) {
        return false;
      }

      if(!this.isLoadedNormal) {
        return false;
      }

      const rush = this.rush;
      if(rush === null) {
        return false;
      }

      return isRushEditable(rush.status);
    },

    /** @returns {boolean} */
    isForceStartButtonVisible() {
      if(!this.mayManageRush) {
        return false;
      }

      if(!this.isLoadedNormal) {
        return false;
      }

      const rush = this.rush;
      if(rush === null) {
        return false;
      }

      return rush.status === RUSH_STATUS__INCOMING;
    },
  },

  created() {
    this.loadRush();
  },

  methods: {
    loadRush() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/rush/${this.rushId}/info`;
      HTTP.get(url)
      .then(response => {
        this.rush = response.data.data;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    getRushStatusClass,

    //-------------------------------------------------------------------------
    // Force start
    //-------------------------------------------------------------------------

    forceStartRushWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      this.confirmRushForceStart(
        this.rushId,
        () => {
          this.forceStartRush();
        },
      );
    },

    /**
     * @param {number} rushId 
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRushForceStart(rushId, onConfirmed) {
      const messageTemplate = `Are you sure you want to force start rush ${rushId} {env}?`;

      confirmAction(
        this,
        messageTemplate,
        'Force start',
        onConfirmed,
      );
    },

    forceStartRush() {
      const rush = this.rush;
      if(rush === null) {
        notifier.showError(`Rush not loaded.`, {sender: this});
        return;
      }

      const url = `/rush/${rush.id}/force_start`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        rush.srtime = data.srtime;
        rush.status = RUSH_STATUS__ACTIVE;
        notifier.showSuccess('Rush started.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },

  filters: {
    getPlayerGroupNameVerbose,
    getRushStatusName,
    getRushRealStartTimeStr,
    getRushRealFinishTimeStr,
  },
};
</script>
